import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useParams } from "react-router-dom";
import {
  AuraLayout,
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  SupportV2Icon,
  Title,
} from "lola-framework-ui-test";
import { Trans, useTranslation } from "react-i18next";
import type { IOperationResponse } from "../services/api";
import { cancelOperation, getOperation } from "../services/api";
import { useOperationSSE } from "../hooks/useOperationSSE";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, TITLE_COLOR, flavorClassName } from "../constants";
import {
  addBusinessDays,
  formatLabeledAmount,
  formatReceiptDate,
} from "../utils";
import { PayoutInfo } from "./transfer/flavor-a/components/PayoutInfo";
import { Popup } from "../components/Popup";
import type { TFunction } from "i18next";
import {
  CheckCircleIcon,
  ErrorCircleIcon,
  LimitInfoIcon,
  MoreCircleIcon,
  WarningCircleIcon,
} from "../assets/icons";
import { BodyCopy } from "../components/BodyCopy";
import { useClaimLink } from "../hooks/useClaimLink";
import type { ISessionState } from "../shared/session/interfaces/session";
import { LegalModal } from "../components/legal/LegalModal";
import "../styles/legal.style.css";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


interface SummaryItem {
  status: string;
  value: string | React.ReactNode;
  isBold?: boolean;
  isComplete?: boolean;
}

const buildStatusField = (status: string) => {
  switch (status) {
    case "completed":
      return {
        icon: <CheckCircleIcon />,
        value: status,
        color: "#31C859",
      };
    case "failed":
    case "error":
      return {
        icon: <ErrorCircleIcon />,
        value: status,
        color: "#FF3B30",
      };
    case "processing":
      return {
        icon: <MoreCircleIcon />,
        value: status,
        color: "#A3A3A3",
      };
    case "cancelled":
      return {
        icon: <ErrorCircleIcon bgColor="#A3A3A3" />,
        value: status,
        color: "#A3A3A3",
      };

    default:
      return {
        icon: <WarningCircleIcon />,
        value: status,
        color: "#F28D19",
      };
  }
};

const buildSummaryItems = (
  operation: IOperationResponse,
  t: TFunction<"translation", undefined>,
  session: ISessionState
): SummaryItem[] => [
  {
    status: t("receiptSummary.fields.status"),
    value: (
      <span
        className="span-status"
        style={{
          color: buildStatusField(operation.status).color,
        }}
      >
        {buildStatusField(operation.status).icon}{" "}
        {t(
          `receiptSummary.fields.status.items.${
            buildStatusField(operation.status).value
          }`
        )}
      </span>
    ),
    isBold: true,
    isComplete: operation.status === "completed",
  },
  {
    status: t("receiptSummary.fields.transfer.amount"),
    value: formatLabeledAmount(operation.sendAmount, operation.sourceCurrency),
  },
  {
    status: t("receiptSummary.fields.transaction.fee"),
    value: formatLabeledAmount(
      operation.transactionFee ?? 0,
      operation.sourceCurrency
    ),
  },
  {
    status: t("receiptSummary.fields.exchange.rate"),
    value: `${operation.exchangeRate.toFixed(4)} ${
      operation.destinationCurrency
    }/${operation.sourceCurrency}`,
  },
  {
    status: t("receiptSummary.fields.total.payment"),
    value: formatLabeledAmount(operation.totalCost, operation.sourceCurrency),
  },
  {
    status: t("receiptSummary.fields.receiving"),
    value: formatLabeledAmount(
      operation.receiveAmount,
      operation.destinationCurrency
    ),
  },
  {
    status: t("receiptSummary.fields.funding.fee"),
    value: formatLabeledAmount(
      operation.fundingFee ?? 0,
      operation.sourceCurrency
    ),
  },
  {
    status: t("receiptSummary.fields.tax.fee"),
    value: formatLabeledAmount(operation.taxFee ?? 0, operation.sourceCurrency),
  },
  {
    status: t("receiptSummary.fields.receiver"),
    value: `${session.data?.userData?.name?.firstName} ${session.data?.userData?.name?.lastName}`,
  },
  {
    status: t("receiptSummary.fields.trasnaction.date"),
    value: `${formatReceiptDate(new Date(operation.createdAt))} \n ${new Date(
      operation.createdAt
    ).toLocaleTimeString()}`,
  },
  {
    status: t("receiptSummary.fields.date.available"),
    value: `${addBusinessDays(new Date(operation.createdAt))}`,
  },
];

export const ReceiptSummary = memo(() => {

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"));
  }, []);
  useClaimLink();
  const { t } = useTranslation();
  const {
    theme,
    operations,
    setOperations,
    setAmountState,
  } = useStore();
  const { session } = useSessionStore();
  const { operationId } = useParams<{ operationId: string }>();
  const { redirectTo } = useRouteWithToken();

  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<"success" | "error" | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  useOperationSSE(
    session.data?.linkId,
    operationId ? { watchOperationId: operationId } : undefined
  );

  const operation = useMemo(
    () => operations.find((op) => op.operationId === operationId) ?? null,
    [operations, operationId]
  );

  const upsertOperation = useCallback(
    (fetched: IOperationResponse) => {
      const current = useStore.getState().operations;
      const exists = current.some((op) => op.operationId === fetched.operationId);
      setOperations(
        exists
          ? current.map((op) =>
              op.operationId === fetched.operationId ? fetched : op
            )
          : [...current, fetched]
      );
    },
    [setOperations]
  );

  const refresh = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || !operationId) return;
    const fetched = await getOperation(linkId, operationId);
    upsertOperation(fetched);
  }, [session.data?.linkId, operationId, upsertOperation]);

  useEffect(() => {
    const linkId = session.data?.linkId;
    if (!linkId || !operationId) return;

    let cancelled = false;
    const hasCached = useStore
      .getState()
      .operations.some((op) => op.operationId === operationId);
    if (!hasCached) setLoading(true);

    getOperation(linkId, operationId)
      .then((fetched) => {
        if (!cancelled) upsertOperation(fetched);
      })
      .catch((err) => {
        console.error("Failed to fetch operation:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [operationId, session.data?.linkId, upsertOperation]);

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    bodyCopy,
    title,
  } = colorConfig;
  const summaryItems = operation
    ? buildSummaryItems(operation, t, session)
    : [];
  const canCancel = operation?.cancellation?.allowed === true;
  const cancelMode = operation?.cancellation?.mode;

  useEffect(() => {
    if (!operation?.cancellation?.allowed || !operation.cancellation.expiresAt) {
      return;
    }
    const ms = new Date(operation.cancellation.expiresAt).getTime() - Date.now();
    if (ms <= 0) return;
    const id = window.setTimeout(() => {
      void refresh();
    }, ms);
    return () => window.clearTimeout(id);
  }, [operation?.cancellation?.allowed, operation?.cancellation?.expiresAt, refresh]);

  const handleTransferAgain = () => {
    setAmountState({
      amount: operation?.sendAmount.toString() ?? "0",
      anchor: "send",
    });
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  };

  const handleCancelConfirm = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || !operationId) return;

    setCancelling(true);
    setCancelResult(null);
    try {
      await cancelOperation(linkId, operationId);
      await refresh();
      setCancelResult("success");
    } catch {
      setCancelResult("error");
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  }, [session.data?.linkId, operationId, refresh]);

  if (!operation) {
    return (
      <AuraLayout
        colorConfig={colorConfig}
        className={`receipt-summary-section white-view-background ${flavorClassName}`}
      >
        <Layout.Header>
          <Navbar
            title=""
            align="center"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
            icon={<SupportV2Icon colors={dropzoneColors} />}
            onIconClick={() => redirectTo(APP_ROUTES.SUPPORT_PAGE)}
          />
        </Layout.Header>
        <Layout.Content className="">
          <section className="receipt-summary-container">
            {loading ? (
              <p className="foreground">
                {t("receiptSummary.loading", "Loading...")}
              </p>
            ) : (
              <p className="foreground">
                {t("receiptSummary.notFound", "Operation not found")}
              </p>
            )}
          </section>
        </Layout.Content>
      </AuraLayout>
    );
  }

  const [intPart, decPart] = operation.sendAmount.toFixed(2).split(".");

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={`receipt-summary-section white-view-background ${flavorClassName}`}
      >
        <Layout.Header>
          <Navbar
            title={t("transfer.summary.title")}
            align="center"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
            icon={<SupportV2Icon colors={dropzoneColors} />}
            onIconClick={() => redirectTo(APP_ROUTES.SUPPORT_PAGE)}
          />
        </Layout.Header>
        <Layout.Content isOverflowauto>
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="receipt-summary-container">
            <section className="receipt-summary-wrapper">
              <section className="summary-section gradient-card">
                <section className="summary-title">
                  <p className="foreground">
                    {t("receiptSummary.youveTransferred", "You sent")}
                  </p>
                  <h1 className="foreground">
                    ${intPart}.<label>{decPart}</label>{" "}
                    {operation.sourceCurrency}
                  </h1>
                  <p className="foreground transaction-id">
                    <b>{t("receiptSummary.transactionId", "Transaction ID")}</b>
                    <span>
                      {operation.operationId.slice(0, 8).toUpperCase()}
                    </span>
                  </p>
                </section>
                <section className="summary-info">
                  {summaryItems.map(
                    ({ status, value, isBold, isComplete }, index) => (
                      <SummaryInfoItem
                        key={index}
                        status={status}
                        value={value}
                        isBold={isBold}
                        isComplete={isComplete}
                      />
                    )
                  )}
                </section>
                {canCancel && !cancelResult && (
                  <button
                    type="button"
                    className="cancel-operation-btn"
                    style={{ color: bodyCopy }}
                    onClick={() => setShowConfirm(true)}
                  >
                    {t("receiptSummary.cancelTransfer")}
                  </button>
                )}
              </section>

              {cancelResult === "success" && (
                <p className="foreground completed cancel-feedback">
                  {t("receiptSummary.cancelSuccess")}
                </p>
              )}
              {cancelResult === "error" && (
                <p className="foreground text-error cancel-feedback">
                  {t("receiptSummary.cancelError")}
                </p>
              )}

              <PayoutInfo
                title={t("receiptSummary.transferAgain", "Transfer again")}
                subtitle=""
                icon={
                  <CashSpecialIcon
                    size={30}
                    className="bg-transparent flavor-c-icon"
                  />
                }
                background={backgroundBtn}
                color={textColorBtn}
                onChange={handleTransferAgain}
              />
            </section>
            <section className="receipt-summary-disclaimers">
              <p className="footer-text muted-foreground text-light">
                <Trans i18nKey="receiptSummary.transferNote" />
              </p>
              <p className="footer-text">
                <GradientText
                  as="button"
                  type="button"
                  className="receipt-cancel-policy"
                  textColor={title}
                  onClick={() => setShowTransactionPolicyModal(true)}
                >
                  {t("receiptSummary.cancelationPolicy", "Cancelation Policy")}
                </GradientText>
              </p>
            </section>
          </section>
        </Layout.Content>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
        <section className="cancel-confirm-slot">
          <LimitInfoIcon
            className="cancel-confirm-icon"
            size={60}
            colors={[TITLE_COLOR, TITLE_COLOR]}
          />
          <Title
            title={t("receiptSummary.cancelConfirmTitle")}
            align="center"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy}>
            {t(
              cancelMode === "request"
                ? "receiptSummary.cancelConfirmMessageRequest"
                : "receiptSummary.cancelConfirmMessageVoid"
            )}
          </BodyCopy>
          <div className="cancel-confirm-actions">
            <Button
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={handleCancelConfirm}
              disabled={cancelling}
            >
              {cancelling ? "..." : t("receiptSummary.cancelConfirmYes")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
              disabled={cancelling}
            >
              {t("receiptSummary.cancelConfirmNo")}
            </Button>
          </div>
        </section>
      </Popup>
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  isComplete?: boolean;
  status: string;
  value: string | React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  isComplete,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p className="foreground status">{status}</p>
    <p
      className={`foreground ${isComplete ? "completed" : "muted-foreground"} ${
        value === "Failed" ? "text-error" : ""
      }`}
      style={{ whiteSpace: "pre-line", textAlign: "right" }}
    >
      {value}
    </p>
  </section>
);
